import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { useAsyncData, type AsyncState } from '@/hooks';
import { BULK_PRODUCT_ACTIONS } from '@/types/platform';
import type {
  BulkApplyGroupProductRequest,
  BulkProductAction,
  BulkProductApplyResult,
  BulkProductSkippedEntry,
  GroupAnalytics,
  ProductCatalogueEntry,
} from '@/types/platform';
import styles from './platform-pages.module.css';

/**
 * The products tab — what the group's institutions hold, from the analytics
 * projection, and the group's own bulk product surface.
 *
 * TWO READINGS, ONE ENDPOINT EACH. The projection (read-only) reports what
 * each member holds, in counts and per-member codes. The bulk endpoint
 * (POST /platform/structure/groups/:groupId/products) applies a SUBSCRIBE or
 * UNSUBSCRIBE across members and answers with a per-member report — applied,
 * skipped with a reason, and ids that are not members at all. The two are
 * different questions, and a stale member list is exactly what
 * `missingInstitutions` is for: the result summary says which ids belonged to
 * no member rather than pretending they were touched.
 *
 * PER-MEMBER PRODUCT STATE STAYS AN INSTITUTION CONCERN. A subscription lives
 * on each institution's own activation row, so the institution's products tab
 * remains the place to walk a single state. What this tab adds is the group
 * view: which member holds which product, and one action that lands across
 * many of them at once. The result of a bulk apply is shown in full, then the
 * projection is refreshed so the tables agree with what core recorded.
 *
 * PRODUCT NAMES COME FROM THE CATALOGUE, NOT THE CODES. A code like ATL-EDU
 * means nothing on screen; the catalogue resolves it. The catalogue and the
 * projection are separate calls, so a code whose definition cannot be fetched
 * is rendered as the code itself — true, if terse — never dropped.
 */

export function GroupProductsPanel({
  groupId,
  analyticsState,
  onRetry,
}: {
  groupId: string;
  analyticsState: AsyncState<GroupAnalytics>;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  const catalogueFetcher = useCallback(() => platformApi.products.catalogue(), []);
  const { state: catalogueState } = useAsyncData<ProductCatalogueEntry[]>(
    `group-products-catalogue:${groupId}`,
    catalogueFetcher,
    analyticsState.status === 'ready',
  );

  const nameFor = (code: string): string => {
    if (catalogueState.status === 'ready') {
      const entry = catalogueState.data.find((candidate) => candidate.code === code);
      if (entry) return entry.name;
    }
    return code;
  };

  const [bulkOpen, setBulkOpen] = useState(false);

  return (
    <Panel
      title={t('platform.groups.products.title')}
      icon="products"
      actions={
        analyticsState.status === 'ready' && analyticsState.data.institutions.length > 0 ? (
          <button type="button" className={styles.primaryBtnSm} onClick={() => setBulkOpen(true)}>
            <Icon name="products" size={15} />
            {t('platform.groups.products.bulkTitle')}
          </button>
        ) : undefined
      }
    >
      <AsyncPanel
        state={analyticsState}
        onRetry={onRetry}
        isEmpty={(analytics) => analytics.productSubscriptions.length === 0}
        emptyTitle={t('platform.groups.products.emptyTitle')}
        emptyBody={t('platform.groups.products.emptyBody')}
      >
        {(analytics) => (
          <>
            <p className={styles.dataNotice}>
              <Icon name="info" size={15} aria-hidden="true" />
              {t('platform.groups.products.readOnlyNotice')}
            </p>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className={styles.tableCaption}>
                  {t('platform.groups.products.tableCaption', { n: analytics.productSubscriptions.length })}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('platform.groups.products.colProduct')}</th>
                    <th scope="col">{t('platform.groups.products.colMembers')}</th>
                    <th scope="col">{t('platform.groups.products.colInstitutions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.productSubscriptions.map((subscription) => {
                    const holders = analytics.institutions.filter((institution) =>
                      (institution.productSubscriptions ?? []).includes(subscription.productCode),
                    );
                    return (
                      <tr key={subscription.productCode}>
                        <th scope="row" className={styles.tableRowHeader}>
                          {nameFor(subscription.productCode)}
                          <span className={styles.tableSub}>{subscription.productCode}</span>
                        </th>
                        <td>{subscription.institutionCount}</td>
                        <td className={styles.mutedCell}>
                          {holders.length > 0 ? holders.map((holder) => holder.name).join(', ') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {analytics.institutions.length > 0 && (
              <>
                <h3 className={styles.subheading}>{t('platform.groups.products.memberTableTitle')}</h3>
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th scope="col">{t('platform.groups.products.colInstitution')}</th>
                        {analytics.productSubscriptions.map((subscription) => (
                          <th scope="col" key={subscription.productCode}>
                            {nameFor(subscription.productCode)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.institutions.map((institution) => {
                        const subscriptions = institution.productSubscriptions ?? [];
                        return (
                          <tr key={institution.institutionId}>
                            <th scope="row" className={styles.tableRowHeader}>
                              {institution.name}
                              <span className={styles.tableSub}>{institution.code}</span>
                            </th>
                            {analytics.productSubscriptions.map((subscription) => {
                              const active = subscriptions.includes(subscription.productCode);
                              return (
                                <td key={subscription.productCode}>
                                  {active ? (
                                    <span className={styles.okTag}>{t('platform.groups.products.stateActive')}</span>
                                  ) : (
                                    <span className={styles.mutedCell}>{t('platform.groups.products.stateNone')}</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </AsyncPanel>

      {bulkOpen && analyticsState.status === 'ready' && (
        <BulkApplyDialog
          groupId={groupId}
          catalogue={catalogueState.status === 'ready' ? catalogueState.data : []}
          members={analyticsState.data.institutions}
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onApplied={onRetry}
        />
      )}
    </Panel>
  );
}

type BulkPhase = 'form' | 'confirm' | 'result';

/**
 * The bulk apply flow: form → confirmation → result, inside one modal.
 *
 * THE CONFIRMATION IS THE REQUEST PREVIEW, NOT A SECOND FORM. The operator
 * picks product, action and scope, then lands on a screen stating exactly what
 * will be sent and how many institutions are in scope; confirming sends it. The
 * result replaces the confirmation in place, so the operator sees the
 * consequence of the exact request they confirmed without navigating anywhere.
 * Skipped members carry a reason code and are labelled from core's vocabulary;
 * a reason this build has not met yet still renders as its own code rather
 * than blanking the row.
 */
function BulkApplyDialog({
  groupId,
  catalogue,
  members,
  open,
  onClose,
  onApplied,
}: {
  groupId: string;
  catalogue: ProductCatalogueEntry[];
  members: GroupAnalytics['institutions'];
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
}) {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<BulkPhase>('form');
  const [productCode, setProductCode] = useState('');
  const [action, setAction] = useState<BulkProductAction>(BULK_PRODUCT_ACTIONS[0]);
  const [scopeAll, setScopeAll] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkProductApplyResult | null>(null);

  const productName = catalogue.find((entry) => entry.code === productCode)?.name ?? productCode;
  const targets = scopeAll
    ? members.map((member) => member.institutionId)
    : selectedIds.filter((id) => members.some((member) => member.institutionId === id));

  const formValid = productCode.length > 0 && targets.length > 0;

  const close = () => {
    if (submitting) return;
    onClose();
    // The next open starts clean: the previous run's product and result are
    // gone, and the projection has been refreshed behind the dialog.
    setPhase('form');
    setProductCode('');
    setAction(BULK_PRODUCT_ACTIONS[0]);
    setScopeAll(true);
    setSelectedIds([]);
    setError(null);
    setResult(null);
  };

  const apply = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const payload: BulkApplyGroupProductRequest = {
      productCode,
      action,
      ...(scopeAll ? {} : { institutionIds: targets }),
    };
    try {
      setResult(await platformApi.structure.bulkApplyGroupProduct(groupId, payload));
      setPhase('result');
    } catch (cause) {
      setError((cause as Error).message);
      setPhase('confirm');
    } finally {
      setSubmitting(false);
    }
  };

  const done = () => {
    onApplied();
    close();
  };

  const actionLabel = t(
    action === 'SUBSCRIBE'
      ? 'platform.groups.products.actionSubscribe'
      : 'platform.groups.products.actionUnsubscribe',
  );

  return (
    <Modal
      open={open}
      onClose={close}
      title={
        phase === 'result'
          ? t('platform.groups.products.resultTitle')
          : phase === 'confirm'
            ? t('platform.groups.products.bulkTitle')
            : t('platform.groups.products.bulkTitle')
      }
      description={phase === 'form' ? t('platform.groups.products.bulkLead') : undefined}
      closeOnBackdropClick={!submitting}
    >
      {phase === 'result' && result ? (
        <>
          <BulkResultSummary result={result} productName={productName} />
          <div className="modal-actions">
            <button type="button" className="modal-button" onClick={done}>
              {t('common.close')}
            </button>
          </div>
        </>
      ) : phase === 'confirm' ? (
        <form
          className={styles.dialogForm}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <p className={styles.dataNotice} role="status">
            <Icon name="info" size={15} aria-hidden="true" />
            {t('platform.groups.products.confirmTitle', { action: actionLabel, product: productName, count: targets.length })}
          </p>
          <p className={styles.rowDetail}>{t('platform.groups.products.confirmBody')}</p>

          {error && (
            <p className={styles.formError} role="alert">
              {error}
            </p>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="modal-button modal-button--secondary"
              onClick={() => setPhase('form')}
              disabled={submitting}
            >
              {t('common.back')}
            </button>
            <button type="submit" className="modal-button" disabled={submitting}>
              {submitting ? t('platform.confirm.submitting') : t('platform.groups.products.confirm')}
            </button>
          </div>
        </form>
      ) : (
        <form
          className={styles.dialogForm}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (formValid) setPhase('confirm');
          }}
        >
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.groups.products.productLabel')}</span>
            <Select
              searchable
              value={productCode}
              onChange={setProductCode}
              placeholder={t('platform.groups.products.productPlaceholder')}
              options={catalogue.map((entry) => ({ value: entry.code, label: entry.name, description: entry.code }))}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.groups.products.actionLabel')}</span>
            <Select
              value={action}
              onChange={(value) => setAction(value as BulkProductAction)}
              options={BULK_PRODUCT_ACTIONS.map((candidate) => ({
                value: candidate,
                label:
                  candidate === 'SUBSCRIBE'
                    ? t('platform.groups.products.actionSubscribe')
                    : t('platform.groups.products.actionUnsubscribe'),
              }))}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.groups.products.scopeLabel')}</span>
            <Select
              value={scopeAll ? 'all' : 'selected'}
              onChange={(value) => setScopeAll(value === 'all')}
              options={[
                { value: 'all', label: t('platform.groups.products.scopeAll') },
                { value: 'selected', label: t('platform.groups.products.scopeSelected') },
              ]}
            />
          </label>

          {!scopeAll && (
            <MemberMultiSelect
              members={members}
              selectedIds={selectedIds}
              onChange={setSelectedIds}
              placeholder={t('platform.groups.products.membersPlaceholder')}
              hint={t('platform.groups.products.membersHint')}
            />
          )}

          {!formValid && (
            <p className={styles.dataNotice}>
              <Icon name="info" size={15} aria-hidden="true" />
              {t('platform.groups.products.nothingChosen')}
            </p>
          )}

          {error && (
            <p className={styles.formError} role="alert">
              {error}
            </p>
          )}

          <div className="modal-actions">
            <button type="button" className="modal-button modal-button--secondary" onClick={close}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="modal-button" disabled={!formValid}>
              {t('platform.groups.products.apply')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/**
 * A multi-select for the member scope, built from checkboxes.
 *
 * The shared Select is single-value, and a multi-select is not a dropdown — it
 * is a list the operator scans while ticking. Search narrows the visible list;
 * the selection survives the query, so checking and searching do not fight.
 */
function MemberMultiSelect({
  members,
  selectedIds,
  onChange,
  placeholder,
  hint,
}: {
  members: GroupAnalytics['institutions'];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  placeholder: string;
  hint: string;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const visible = q
    ? members.filter((member) => member.name.toLowerCase().includes(q) || member.code.toLowerCase().includes(q))
    : members;

  const toggle = (id: string) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((candidate) => candidate !== id) : [...selectedIds, id]);
  };

  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{t('platform.groups.products.membersLabel')}</span>
      <input
        className={styles.input}
        value={query}
        placeholder={placeholder}
        onChange={(event) => setQuery(event.target.value)}
      />
      <ul className={styles.checklist} role="group" aria-label={t('platform.groups.products.membersLabel')}>
        {visible.length === 0 ? (
          <li className={styles.rowDetail}>{t('common.noResults')}</li>
        ) : (
          visible.map((member) => (
            <li key={member.institutionId} className={styles.checklistItem}>
              <label className={styles.checklistLabel}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(member.institutionId)}
                  onChange={() => toggle(member.institutionId)}
                />
                <span>
                  {member.name}
                  <span className={styles.checklistMeta}>{member.code}</span>
                </span>
              </label>
            </li>
          ))
        )}
      </ul>
      <span className={styles.fieldHint}>{hint}</span>
    </div>
  );
}

/**
 * The per-member outcome, read out loud rather than reduced to a status line.
 *
 * `applied` names the members the action took effect on; `skipped` carries the
 * server's reason for each member it could not touch; `missingInstitutions`
 * are ids that belong to no member at all — the caller's membership list was
 * stale, which is exactly what an operator needs to know about.
 */
function BulkResultSummary({ result, productName }: { result: BulkProductApplyResult; productName: string }) {
  const { t } = useTranslation();

  const renderSkipped = (skipped: BulkProductSkippedEntry) => (
    <li key={skipped.institutionId} className={styles.row}>
      <div className={styles.rowMain}>
        <p className={styles.rowTitle}>{skipped.institutionId}</p>
        <p className={styles.rowMetaLine}>
          {t(`platform.groups.products.skippedReason.${skipped.reason}`, { defaultValue: skipped.reason })}
        </p>
      </div>
    </li>
  );

  return (
    <>
      <p className={styles.dataNotice} role="status">
        <Icon name="checkmark" size={15} aria-hidden="true" />
        {t('platform.groups.products.resultApplied', { n: result.applied.length })}
        {result.skipped.length > 0
          ? ` · ${t('platform.groups.products.resultSkipped', { n: result.skipped.length })}`
          : ''}
        {result.missingInstitutions.length > 0
          ? ` · ${t('platform.groups.products.resultMissing', { n: result.missingInstitutions.length })}`
          : ''}
        <span className={styles.tableSub}> {productName}</span>
      </p>

      {result.applied.length > 0 && (
        <ul className={styles.rowList}>
          {result.applied.map((entry) => (
            <li key={entry.institutionId} className={styles.row}>
              <div className={styles.rowMain}>
                <p className={styles.rowTitle}>{entry.institutionId}</p>
                <p className={styles.rowMetaLine}>{t('platform.groups.products.outcomeApplied')}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {result.skipped.length > 0 && (
        <>
          <h3 className={styles.subheading}>
            {t('platform.groups.products.resultSkipped', { n: result.skipped.length })}
          </h3>
          <ul className={styles.rowList}>{result.skipped.map(renderSkipped)}</ul>
        </>
      )}

      {result.missingInstitutions.length > 0 && (
        <>
          <h3 className={styles.subheading}>
            {t('platform.groups.products.resultMissing', { n: result.missingInstitutions.length })}
          </h3>
          <p className={styles.rowDetail}>{result.missingInstitutions.join(', ')}</p>
        </>
      )}
    </>
  );
}
